import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/authContext";
import HotelReserve from "./HotelReserve";
import { formatMoney } from "../../utils/globalTravel";
import { BASE_URL } from "../../utils/config";

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80";

const toLocalDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const createDefaultStay = () => {
  const checkIn = new Date();
  checkIn.setDate(checkIn.getDate() + 1);
  const checkOut = new Date(checkIn);
  checkOut.setDate(checkOut.getDate() + 1);
  return { checkInDate: toLocalDateInput(checkIn), checkOutDate: toLocalDateInput(checkOut) };
};

const hotelImageUrl = (image) => {
  if (!image) return FALLBACK_IMAGE;
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  let configuredBase = BASE_URL;
  while (configuredBase.endsWith("/")) configuredBase = configuredBase.slice(0, -1);
  const apiBase = configuredBase.endsWith("/api") ? configuredBase : `${configuredBase}/api`;
  return `${apiBase}/hotels/images/${image}`;
};

const HotelView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [openBook, setOpenBook] = useState(false);
  const { id } = useParams();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const suppliedStay = location.state;
  const fallbackStay = createDefaultStay();
  const stay = suppliedStay?.checkInDate && suppliedStay?.checkOutDate
    && suppliedStay.checkOutDate > suppliedStay.checkInDate
    ? suppliedStay
    : fallbackStay;

  const start = new Date(`${stay.checkInDate}T00:00:00Z`);
  const end = new Date(`${stay.checkOutDate}T00:00:00Z`);
  const calculatedNights = Math.round((end.getTime() - start.getTime()) / 86400000);
  const dayDifference = Number.isFinite(calculatedNights) && calculatedNights > 0 ? calculatedNights : 1;

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`/hotels/find/${id}`)
      .then((response) => {
        if (!active) return;
        if (!response.data) throw new Error("This hotel is not available anymore.");
        setData(response.data);
      })
      .catch((error) => {
        if (active) setLoadError(error.response?.data?.message || error.message || "Unable to load this hotel.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [id]);

  const handleReserve = () => {
    if (!user) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    setOpenBook(true);
  };

  if (loading) return <main className="mx-auto max-w-6xl px-4 py-16 text-slate-600">Loading hotel details…</main>;
  if (loadError || !data) return (
    <main className="mx-auto max-w-3xl px-4 py-16">
      <section className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
        <h1 className="text-xl font-bold">Hotel unavailable</h1>
        <p className="mt-2">{loadError || "This hotel could not be found."}</p>
        <button type="button" onClick={() => navigate("/hotelhome")} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white">Browse hotels</button>
      </section>
    </main>
  );

  return (
    <main className="min-h-screen bg-white">
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:py-12">
        <button type="button" onClick={() => navigate("/hotelhome")} className="mb-6 text-sm font-semibold text-blue-700 hover:underline">← Back to hotels</button>
        <div className="grid items-start gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <img
              src={hotelImageUrl(data.HotelImg)}
              onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = FALLBACK_IMAGE; }}
              alt={data.name ? `${data.name} hotel` : "Hotel"}
              className="max-h-[560px] w-full rounded-2xl object-cover shadow-sm"
            />
            {Array.isArray(data.HotelImgs) && data.HotelImgs.length > 0 && (
              <>
                <h2 className="mt-8 text-xl font-bold text-slate-900">More photos</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {data.HotelImgs.map((image, index) => (
                    <img key={image || index} src={hotelImageUrl(image)} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = FALLBACK_IMAGE; }} alt={`${data.name || "Hotel"} photo ${index + 1}`} loading="lazy" className="h-36 w-full rounded-xl object-cover" />
                  ))}
                </div>
              </>
            )}
          </div>
          <section className="rounded-2xl border border-slate-200 p-6 shadow-sm sm:p-8">
            <p className="text-sm font-bold uppercase tracking-widest text-blue-600">{data.type || "Accommodation"}</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">{data.name}</h1>
            <p className="mt-2 text-slate-600">{[data.city, data.country].filter(Boolean).join(", ")}</p>
            <p className="mt-5 leading-7 text-slate-700">{data.description || data.title}</p>
            {data.distance && <p className="mt-4 text-sm text-slate-500">Approximately {data.distance} from the city centre.</p>}
            <div className="mt-6 rounded-xl bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Your selected stay</p>
              <p className="mt-1 font-semibold text-slate-900">{stay.checkInDate} to {stay.checkOutDate}</p>
              <p className="mt-1 text-sm text-slate-600">{dayDifference} night(s)</p>
            </div>
            <p className="mt-6 text-2xl font-bold text-slate-950">{formatMoney(Number(data.cheapestPrice || 0) * dayDifference, data.currency || "USD")} <span className="text-sm font-normal text-slate-500">estimated from / {dayDifference} night(s)</span></p>
            <button type="button" onClick={handleReserve} className="mt-6 w-full rounded-xl bg-blue-600 px-5 py-4 font-bold text-white transition hover:bg-blue-700">
              Reserve now
            </button>
            <p className="mt-3 text-xs leading-5 text-slate-500">You can review available rooms before submitting your reservation request. Travelco payment processing is not enabled yet.</p>
          </section>
        </div>
      </section>
      {openBook && (
        <HotelReserve
          setOpen={setOpenBook}
          hotelId={id}
          checkInDate={stay.checkInDate}
          checkOutDate={stay.checkOutDate}
          date_difference={dayDifference}
          currency={data.currency || "USD"}
        />
      )}
    </main>
  );
};

export default HotelView;
